import React, { useState } from "react";

// Mini donut chart SVG
function DonutChart({ segments, size = 80 }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;
  const r = 28, cx = 40, cy = 40, stroke = 10;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80">
      {segments.map((seg, i) => {
        const pct = seg.value / total;
        const dash = pct * circ;
        const gap = circ - dash;
        const el = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset * circ / total * r * 2 * Math.PI / (2 * Math.PI)}
            style={{ transform: `rotate(${offset * 360 / total - 90}deg)`, transformOrigin: "40px 40px" }}
          />
        );
        offset += seg.value;
        return el;
      })}
      <text x="40" y="44" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1e293b">{total.toLocaleString()}</text>
    </svg>
  );
}

// Mini horizontal bar
function MiniBar({ label, value, max, color }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="mini-bar-row">
      <div className="mini-bar-label">{label}</div>
      <div className="mini-bar-track">
        <div className="mini-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="mini-bar-value">{value.toLocaleString()}</div>
    </div>
  );
}

// Severity badge strip
function SeverityStrip({ items }) {
  return (
    <div className="severity-strip">
      {items.map((item, i) => (
        <div key={i} className="severity-item" style={{ borderColor: item.color }}>
          <span className="severity-dot" style={{ background: item.color }} />
          <span className="severity-count" style={{ color: item.color }}>{item.value}</span>
          <span className="severity-name">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// Widget type renderers
const WIDGET_RENDERERS = {
  w1: () => (
    <div className="widget-visual">
      <DonutChart segments={[
        { value: 2, color: "#6366f1" },
        { value: 2, color: "#e2e8f0" },
      ]} />
      <div className="widget-legend">
        <div className="legend-item"><span className="legend-dot" style={{ background: "#6366f1" }} />Connected <strong>2</strong></div>
        <div className="legend-item"><span className="legend-dot" style={{ background: "#e2e8f0" }} />Not Connected <strong>2</strong></div>
      </div>
    </div>
  ),
  w2: () => (
    <div className="widget-visual">
      <DonutChart segments={[
        { value: 1689, color: "#ef4444" },
        { value: 681, color: "#f59e0b" },
        { value: 36, color: "#94a3b8" },
        { value: 7253, color: "#22c55e" },
      ]} size={80} />
      <div className="widget-legend small">
        <div className="legend-item"><span className="legend-dot" style={{ background: "#ef4444" }} />Failed <strong>1,689</strong></div>
        <div className="legend-item"><span className="legend-dot" style={{ background: "#f59e0b" }} />Warning <strong>681</strong></div>
        <div className="legend-item"><span className="legend-dot" style={{ background: "#22c55e" }} />Passed <strong>7,253</strong></div>
        <div className="legend-item"><span className="legend-dot" style={{ background: "#94a3b8" }} />N/A <strong>36</strong></div>
      </div>
    </div>
  ),
  w3: () => (
    <div className="widget-empty-state">
      <div className="empty-icon">📊</div>
      <p>No namespace alert data available</p>
      <button className="empty-action">Configure Alerts</button>
    </div>
  ),
  w4: () => (
    <div className="widget-empty-state">
      <div className="empty-icon">🖥</div>
      <p>No workload alert data available</p>
      <button className="empty-action">Connect Workloads</button>
    </div>
  ),
  w5: () => (
    <div className="widget-visual vertical">
      <div className="vuln-total">
        <span className="vuln-num">1,470</span>
        <span className="vuln-sub">Total Vulnerabilities</span>
      </div>
      <div style={{ width: "100%" }}>
        <MiniBar label="Critical" value={9} max={1470} color="#ef4444" />
        <MiniBar label="High" value={150} max={1470} color="#f59e0b" />
        <MiniBar label="Medium" value={311} max={1470} color="#6366f1" />
        <MiniBar label="Low" value={1000} max={1470} color="#22c55e" />
      </div>
    </div>
  ),
  w6: () => (
    <div className="widget-visual vertical">
      <div className="vuln-total">
        <span className="vuln-num">2</span>
        <span className="vuln-sub">Total Images Scanned</span>
      </div>
      <SeverityStrip items={[
        { label: "Critical", value: 2, color: "#ef4444" },
        { label: "High", value: 2, color: "#f59e0b" },
      ]} />
    </div>
  ),
};

export default function Widget({ widget, categoryId, onRemove }) {
  const [hovered, setHovered] = useState(false);
  const Renderer = WIDGET_RENDERERS[widget.id];

  return (
    <div
      className="widget"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="widget-header">
        <span className="widget-name">{widget.name}</span>
        <button
          className={`widget-remove-btn ${hovered ? "visible" : ""}`}
          onClick={() => onRemove(categoryId, widget.id)}
          title="Remove widget"
        >
          ✕
        </button>
      </div>
      <div className="widget-body">
        {Renderer ? (
          <Renderer />
        ) : (
          <p className="widget-text">{widget.text}</p>
        )}
      </div>
    </div>
  );
}
